import React, { useState, useCallback, useEffect } from 'react';
import {
  useSearchParams,
  useNavigate,
  Link as RouterLink,
} from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  HealthAndSafetyRounded,
  AutoAwesomeRounded,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import Logo from 'components/styled/Logo';
import SharedRecordSummary from 'components/dashboard/healthRecordsShare/SharedRecordSummary';
import {
  consumeShareCodeAndFetchRecord,
  getTokenValidityLabel,
} from 'services/healthRecordsShareService';
import { setPendingSharedRecord } from 'services/sharedRecordHandoff';

/**
 * Public provider-facing landing page (route: /share?c=NNNNNNN).
 *
 * Reached when a provider scans the QR a patient generated on "Share with a
 * provider". The provider signs in (or creates an account), redeems the
 * single-use code, and reads the consented record right here. From here they can
 * start a ChartMind encounter seeded with the same record.
 *
 * TODO(security): redemption is auth-gated and audited, but any signed-up account
 * can currently redeem. Before wider rollout, gate to verified provider accounts
 * (e.g. NPI lookup or admin approval). Tracked with the client.
 */
const ShareLandingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userLoading } = useAuth();

  const code = (searchParams.get('c') || '').replace(/\D/g, '');
  // Set by /verify when a provider lands here straight from the email link. They
  // already have an account; this context just lacks a session, so greet them as a
  // returning user (sign-in only) instead of a first-time visitor.
  const justVerified = searchParams.get('verified') === '1';

  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Reaching this page consumes any pending post-login redirect, so a later visit
  // to the dashboard doesn't bounce back here again. (Set in stashShareReturn.)
  useEffect(() => {
    localStorage.removeItem('pendingShareReturn');
  }, []);

  const handleRedeem = useCallback(async () => {
    setRedeeming(true);
    setError(null);
    try {
      const data = await consumeShareCodeAndFetchRecord({ manualCode: code });
      setResult(data);
    } catch (err) {
      setError(err.message || 'Could not open this shared record. Please try again.');
    } finally {
      setRedeeming(false);
    }
  }, [code]);

  const handleStartEncounter = useCallback(() => {
    setPendingSharedRecord(result);
    navigate('/dashboard/chartmind');
  }, [result, navigate]);

  // Persist the intended role so it survives the email-verification round-trip
  // (which drops the ?role= query before /onboarding). localStorage, not
  // sessionStorage: clicking the verification link in an email opens a fresh tab
  // with empty sessionStorage, so the hint has to live in shared, durable storage.
  // Remember where to come back to so we can return the provider to this exact
  // shared record once they finish signing in (consumed in DashboardPage).
  const stashShareReturn = useCallback(() => {
    if (code.length === 7) {
      localStorage.setItem('pendingShareReturn', `/share?c=${code}`);
    }
  }, [code]);

  const handleCreateProviderAccount = useCallback(() => {
    localStorage.setItem('pendingSignupRole', 'provider');
    stashShareReturn();
    // Pass the code in the URL too — it's the only breadcrumb that survives the
    // email-verification round-trip on a different device or in-app browser.
    navigate(`/auth?role=provider&c=${code}`);
  }, [navigate, stashShareReturn, code]);

  const renderBody = () => {
    if (userLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (code.length !== 7) {
      return (
        <Alert severity="error">
          This share link is missing a valid code. Please ask the patient to show
          their share code again.
        </Alert>
      );
    }

    // Redeemed — show the consented record read-only.
    if (result) {
      return (
        <Stack spacing={2}>
          {/* Shared with the in-app import dialog, so a provider sees the same record
              whether they arrived with an account or without one. This page used to omit
              the CareStart self-assessment entirely. */}
          <SharedRecordSummary result={result} />

          <Button
            variant="contained"
            size="large"
            startIcon={<AutoAwesomeRounded />}
            onClick={handleStartEncounter}
          >
            Start a ChartMind encounter
          </Button>
          <Typography variant="caption" color="text.secondary">
            This record loads into a new encounter. This code is now used and
            cannot be opened again.
          </Typography>
        </Stack>
      );
    }

    // Signed out — explain what this is and route to auth.
    if (!user) {
      return (
        <Stack spacing={2}>
          {justVerified && (
            <Alert severity="success">
              Your email is verified. Just sign in to open the shared record.
            </Alert>
          )}
          {!justVerified && (
            <Typography variant="body1">
              A patient wants to share their medical record with you. Create a
              free provider account to view it.
            </Typography>
          )}
          {justVerified ? (
            <Button
              component={RouterLink}
              to={`/auth?role=provider&c=${code}`}
              variant="contained"
              onClick={stashShareReturn}
            >
              Sign in
            </Button>
          ) : (
            <Stack spacing={1}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleCreateProviderAccount}
              >
                Create a provider account
              </Button>
              <Button
                component={RouterLink}
                to={`/auth?role=provider&c=${code}`}
                variant="text"
                size="small"
                onClick={stashShareReturn}
                sx={{ alignSelf: 'center' }}
              >
                Already have an account? Sign in
              </Button>
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary">
            We'll bring you right back here afterward.
          </Typography>
        </Stack>
      );
    }

    // Signed in, not yet redeemed.
    return (
      <Stack spacing={2}>
        <Typography variant="body1">
          A patient wants to share their medical record with you. Opening it uses a
          single-use code that expires {getTokenValidityLabel()} after it was
          created.
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <LoadingButton
          variant="contained"
          size="large"
          loading={redeeming}
          onClick={handleRedeem}
        >
          View shared record
        </LoadingButton>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 2,
      }}
    >
      <Paper
        variant="outlined"
        sx={{ p: { xs: 3, sm: 4 }, my: { xs: 2, sm: 6 }, maxWidth: 600, width: '100%' }}
      >
        <Box sx={{ mb: 2.5 }}>
          <Logo />
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <HealthAndSafetyRounded sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700}>
            Shared medical record
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          A patient is securely sharing their record with you through My HealthDesk.
        </Typography>
        {renderBody()}
      </Paper>
    </Box>
  );
};

export default ShareLandingPage;
